import { useState } from "react"
import { Sparkles } from "lucide-react"
import { generateSeason } from "@/lib/api"
import { SafetyDialog } from "@/components/ConfirmDialog"
import { useReadOnly } from "@/lib/read-only"
import { toast } from "sonner"

interface GenerateSeasonButtonProps {
  userId: string
  userLabel: string
  /** Called once the new season is saved so the parent can reload. */
  onGenerated: () => void | Promise<void>
  /** Visual variant. `cta` = larger primary in empty states. `inline` = small button. */
  variant?: "cta" | "inline"
  /** Set true to allow overwriting an existing season (default false). */
  allowOverwrite?: boolean
}

export function GenerateSeasonButton({
  userId,
  userLabel,
  onGenerated,
  variant = "cta",
  allowOverwrite = false,
}: GenerateSeasonButtonProps) {
  const { readOnly } = useReadOnly()
  const [showDialog, setShowDialog] = useState(false)
  const [isGenerating, setIsGenerating] = useState(false)

  const handleGenerate = async () => {
    setIsGenerating(true)
    try {
      await generateSeason(userId, allowOverwrite)
      toast.success(`Season generated for ${userLabel}`)
      setShowDialog(false)
      await onGenerated()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Generation failed")
    } finally {
      setIsGenerating(false)
    }
  }

  const baseClasses =
    variant === "cta"
      ? "inline-flex items-center gap-2 rounded-md bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
      : "inline-flex items-center gap-1.5 rounded-md bg-emerald-600 px-3 py-1.5 text-sm text-white hover:bg-emerald-700 disabled:opacity-50"

  return (
    <>
      <button
        onClick={() => setShowDialog(true)}
        disabled={readOnly || isGenerating}
        className={baseClasses}
        title={readOnly ? "Read-only mode active" : "Generate this user's first workout season"}
      >
        <Sparkles className={variant === "cta" ? "size-4" : "size-3.5"} />
        {isGenerating ? "Generating..." : "Generate Season"}
      </button>

      {showDialog && (
        <SafetyDialog
          title={`Generate season for ${userLabel}`}
          description={
            allowOverwrite
              ? "This will overwrite the existing season via the agentic pipeline."
              : "Calls the agentic pipeline to build the user's first workout season. The plan is written to Supabase as soon as generation succeeds."
          }
          warnings={[
            "Triggers a real Claude generation (counts toward usage).",
            allowOverwrite
              ? "The existing season will be replaced; rollback requires the pre-migration snapshot script."
              : "If the user already has a draft, this will not overwrite it.",
          ]}
          confirmLabel="Generate"
          variant={allowOverwrite ? "destructive" : "default"}
          onConfirm={handleGenerate}
          onCancel={() => setShowDialog(false)}
          isLoading={isGenerating}
        />
      )}
    </>
  )
}
